/**
 * The creator studios' chrome, expressed as semantic roles over the app theme.
 *
 * A studio is app furniture, not content: the header, the toolbars, the sheets,
 * the fields a creator types into. All of it therefore has to wear the *app's*
 * appearance — light, dim or dark, whichever the reader has chosen in Settings —
 * the way every other screen does. Before this module both studios carried their
 * own hard-coded navy palette, so they stayed dark on a light device and drifted
 * from the design system everywhere else.
 *
 * Nothing here is a new colour. Each role is read off the active ALF theme —
 * `atoms` for text and hairlines, the `contrast_*` ramp for the surfaces — which
 * means the studios follow Settings → Appearance for free, and a restyle of the
 * app restyles them too.
 *
 * The ramp direction flips with the scheme (in light `contrast_100` is *darker*
 * than `contrast_0`; in dark it is *lighter*), so the layered surfaces are built
 * with an explicit per-scheme choice rather than an offset. Every pick below
 * keeps the same *relationship* in both directions, which is what makes light
 * mode a mirror of dark rather than a lightened copy of it:
 *
 *     desk (stage) → panel (frame) → card (surface) → field (fill)
 *     #DCE2EA      → #F9FAFB       → #FFFFFF        → #EFF2F6   light
 *     #151D28      → #1C2736       → #222E3F        → #2C3A4E   dim
 *
 * `screen` sits outside that ramp: it is not a step the studio chose but the
 * theme's own background, read straight off the active theme.
 */
import {type Theme, utils} from '@bsky.app/alf'

import {darken, hexToRgb, rgbToHex} from '#/alf/util/colorGeneration'

export type StudioChrome = {
  /** Which side of the theme we resolved to. */
  scheme: 'light' | 'dark'
  /** True for `dim` and `dark` — the escape hatch for the few places that
   * genuinely need to branch rather than swap a token. */
  isDark: boolean

  // ── Surfaces ────────────────────────────────────────────────────────────
  /** The studio's own panel: toolbars, the framed area, the page bar. */
  frame: string
  /** The recessed desk the studio panel floats on. */
  stage: string
  /**
   * The app's own screen colour — the theme background every ordinary screen
   * paints (`atoms.bg`), so a surface wearing it reads as part of the screen
   * rather than as a panel floating on a desk. The FlashBook page sheet uses it:
   * the page a creator writes on should continue the app, not sink into it.
   *
   * In the dark schemes this coincides with `stage` (ALF's dark `contrast_0`
   * *is* the app background); in light they diverge, because `stage` is
   * deliberately recessed a step below white.
   */
  screen: string
  /** A raised surface: cards, bottom sheets, blocks. */
  surface: string
  /** A field or a quiet row: inputs, chips, block headers. */
  fill: string
  /** The pressed/selected version of `fill`. */
  fillStrong: string

  // ── Lines ───────────────────────────────────────────────────────────────
  /**
   * The quiet hairline: a line *between* two surfaces, a rule under a header, the
   * edge of a sheet. Not an outline for a filled box — see `borderStrong`.
   */
  border: string
  /**
   * The firm hairline: the edge of a card, and the edge of a *field*.
   *
   * A field needs this one and not `border`, because `border` is ALF's
   * low-contrast line and in the dark schemes that is the same colour as `fill`
   * — measured 1.00:1, an edge that paints nothing, on a field whose only other
   * separation from its card is a 1.13:1 step of the ramp. It is also the value
   * the block cards are edged with, so a field and a card are firm to the same
   * degree.
   */
  borderStrong: string

  // ── Ink ─────────────────────────────────────────────────────────────────
  text: string
  textMuted: string
  textFaint: string

  // ── Accent ──────────────────────────────────────────────────────────────
  /** Accent as ink: icons, labels, borders. */
  accent: string
  /** Accent as a solid fill, for a committing button. */
  accentFill: string
  /** Ink on `accentFill`. */
  onAccent: string
  /** Accent as a translucent fill, for a selected row. */
  accentTint: string
  /** Accent as a translucent border, for a selected row. */
  accentBorder: string

  // ── Status ──────────────────────────────────────────────────────────────
  danger: string
  dangerTint: string
  dangerBorder: string
  warning: string
  warningTint: string
  warningBorder: string
  success: string
  successTint: string
  successBorder: string

  // ── Floats ──────────────────────────────────────────────────────────────
  /** Backdrop behind a modal sheet. */
  scrim: string
  /**
   * The lift under a card that has to read as a card on the page, as a CSS
   * `box-shadow` value.
   *
   * It is a *pair* on purpose: a tight contact shadow draws the boundary where
   * the card meets the page, and a wide one gives it depth. The light scheme is
   * where this does real work — there a card sits on the app's own background, so
   * a white card on a white page has no edge except its hairline (about 1.25:1,
   * which the eye reads as nothing) — while on the dark schemes it is mostly
   * depth. Unlike the other roles this one is a ready-made value rather than a
   * colour, because a shadow is only ever meaningful as a whole.
   */
  cardShadow: string
  /** A control floating over the book canvas, where the page behind it is the
   * theme's own paper and cannot be assumed to be either light or dark. */
  canvas: string
  canvasBorder: string
  onCanvas: string
  onCanvasMuted: string
  /**
   * The committing version of `canvas`: the float that deletes something.
   *
   * It is opaque for the same reason the rest of that family is — it can be drawn
   * over a video, a photo or a page of any colour — and it is a shade deeper in
   * the light scheme rather than a tint of the chrome, because a bright red beside
   * a light studio glares. Both studios had this value written out by hand, which
   * is why it is a role: one shape, two call sites, and nothing to drift.
   */
  canvasDanger: string
  shadow: string
}

/**
 * Pure so it can be unit-tested for every theme without a React tree.
 */
export function buildStudioChrome(theme: Theme): StudioChrome {
  const {atoms, palette} = theme
  const isDark = theme.scheme === 'dark'
  const white = palette.white

  // In ALF's dark palettes the *lighter* primary is `primary_600` (the ramp is
  // inverted), so the accent that reads as ink follows the scheme, while the
  // accent that fills a button is always `primary_500`.
  const accentInk = isDark ? palette.primary_600 : palette.primary_500
  const dangerInk = isDark ? palette.negative_600 : palette.negative_500
  // Amber is absent from the palette; the two values are the readable ends of
  // the same hue for dark and light paper.
  const warningInk = isDark ? '#FBBF24' : '#B45309'
  // Green needs no per-scheme branch: ALF's dark palettes already swap the ramp
  // (`positive_600` in dark *is* the light palette's `positive_400`, a bright
  // green), so the same name is the readable end on both sides of the mirror.
  // That is the point of `success` being a role rather than a raw ramp step —
  // the bright-but-unreadable `positive_500` is right for a *fill*, wrong for
  // ink, and reading it off the ramp here is what keeps money amounts, tallies
  // and tick marks legible on a light studio instead of a pale wash.

  return {
    scheme: theme.scheme,
    isDark,

    frame: palette.contrast_25,
    stage: isDark ? palette.contrast_0 : palette.contrast_100,
    screen: atoms.bg.backgroundColor,
    surface: isDark ? palette.contrast_50 : palette.contrast_0,
    fill: isDark ? palette.contrast_100 : palette.contrast_50,
    fillStrong: isDark ? palette.contrast_200 : palette.contrast_100,

    border: atoms.border_contrast_low.borderColor,
    borderStrong: atoms.border_contrast_medium.borderColor,

    text: atoms.text.color,
    textMuted: atoms.text_contrast_medium.color,
    textFaint: atoms.text_contrast_low.color,

    accent: accentInk,
    accentFill: palette.primary_500,
    onAccent: white,
    accentTint: utils.alpha(palette.primary_500, isDark ? 0.2 : 0.1),
    accentBorder: utils.alpha(palette.primary_500, isDark ? 0.55 : 0.4),

    danger: dangerInk,
    dangerTint: utils.alpha(palette.negative_500, isDark ? 0.24 : 0.12),
    dangerBorder: utils.alpha(palette.negative_500, isDark ? 0.45 : 0.35),
    warning: warningInk,
    warningTint: utils.alpha(warningInk, isDark ? 0.16 : 0.12),
    warningBorder: utils.alpha(warningInk, isDark ? 0.4 : 0.45),
    success: palette.positive_600,
    // The wash and hairline that go with `success`, matching the `danger` pair
    // above: a *fill* green behind a tick or a "top up" affordance, not the ink
    // `success` is. Without these a screen wanting a green-tinted panel has to
    // name a green, which is how one ends up baked into the Wallet.
    successTint: utils.alpha(palette.positive_500, isDark ? 0.24 : 0.12),
    successBorder: utils.alpha(palette.positive_500, isDark ? 0.45 : 0.35),

    scrim: isDark ? 'rgba(3,7,16,0.72)' : 'rgba(15,23,42,0.45)',
    // The dark schemes need far less: there the card's own surface is already a
    // step above the page, so the shadow only has to stop the two reading as one
    // plane. Push it further and it smears into a dark halo instead of an edge.
    cardShadow: isDark
      ? '0px 1px 3px rgba(0,0,0,0.45), 0px 8px 20px -10px rgba(0,0,0,0.5)'
      : '0px 1px 2px rgba(15,23,42,0.12), 0px 8px 20px -10px rgba(15,23,42,0.32)',
    canvas: isDark ? 'rgba(8,14,26,0.86)' : 'rgba(15,23,42,0.86)',
    canvasBorder: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.24)',
    onCanvas: white,
    onCanvasMuted: 'rgba(255,255,255,0.75)',
    canvasDanger: isDark ? 'rgba(52,10,14,0.92)' : 'rgba(120,26,32,0.94)',
    shadow: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(15,23,42,0.16)',
  }
}

/**
 * The contrast a category accent has to clear to be drawn as *ink* on a light
 * studio: the WCAG body-text figure, because that is what it is used for — a
 * block type's name, a theme's name, a section label.
 */
const INK_CONTRAST = 4.5

/**
 * A category accent — a block type's colour, a theme's colour — tuned for the
 * surface it is about to be drawn on, as ink: a label, an icon, a border.
 *
 * These colours are the studio's own taxonomy (quote orange, table teal…) and
 * are not part of the app palette. They were picked as *washes* to sit on a dark
 * card, so measured against the light studio's frame every one of them is too
 * pale to read as a label — 2.18:1 for the true/false green, 2.65:1 for
 * accordion, and nothing above 4.27:1 — while the darkest of them still looks
 * perfectly reasonable in a swatch.
 *
 * A threshold test cannot catch that: "is this colour pale?" asks about the
 * colour, and these are not pale — they are mid-tone, which is exactly the band
 * that disappears on white and no swatch reviewer notices. So the rule is about
 * the *relationship* instead: keep moving the lightness until the result reads
 * on this surface, and hand back the colour untouched when it already does.
 * Values that pass are preserved exactly, so a brand hue that is already legible
 * is never quietly repainted.
 *
 * It is the same defect on the dark schemes, which is why the rule is not
 * "darken": the taxonomy is mid-tone, so the indigo and the plain text grey are
 * too *dark* to read on the dark studio's frame (3.38:1 and 3.17:1) at the same
 * time as they are too pale for the light one. Which way "readable" lies is a
 * property of the surface, so it is read off the surface rather than guessed
 * from the scheme's name.
 */
export function readableAccent(color: string, chrome: StudioChrome): string {
  return inkOn(color, chrome.frame, INK_CONTRAST)
}

/**
 * An accent used as *ink* on an ordinary screen: the same hue, stepped until it
 * reads on both of the neutrals the app paints behind content.
 *
 * `readableAccent` tunes against `chrome.frame`, which is right for a label on
 * the studio panel but not sufficient for a screen. An app screen has two
 * neutrals an accent can land on — the screen's own `frame` and the raised
 * `surface` — and *which of the two is harder depends on which way the ink has
 * to move*. In light the frame is the darker of the two, so it is the harder one
 * for dark ink; in the dark schemes the surface is the brighter one, so it is the
 * harder one for light ink. Tuning against either alone passes one and misses
 * the other by a hair: an accent tuned on the white surface measured 4.46:1
 * where it actually rendered on the frame.
 *
 * So the target is whichever neutral sits closer to the ink's own lightness —
 * the one that minimises the ratio — which is what makes this correct in both
 * directions without a per-theme rule.
 */
export function readableInkOn(color: string, chrome: StudioChrome): string {
  return inkOn(color, harderBackdrop(color, chrome), INK_CONTRAST)
}

/**
 * The chrome neutral an ink is harder to read on: `frame` or `surface`,
 * whichever is nearer the ink's own lightness.
 */
function harderBackdrop(color: string, chrome: StudioChrome): string {
  const ink = wcagLuminance(color)
  const byFrame = Math.abs(wcagLuminance(chrome.frame) - ink)
  const bySurface = Math.abs(wcagLuminance(chrome.surface) - ink)
  return byFrame <= bySurface ? chrome.frame : chrome.surface
}

/**
 * `color` stepped in HSL lightness until it clears `target` on `surface`, in
 * whichever direction that surface needs.
 */
function inkOn(color: string, surface: string, target: number): string {
  if (contrastRatio(color, surface) >= target) return color
  // A light surface needs the ink darker (a positive amount), a dark one needs
  // it lighter (a negative one).
  const towardsReadable = wcagLuminance(surface) < 0.5 ? -1 : 1
  // Two-point steps: fine enough that the result stays as close to the original
  // hue as the target allows, coarse enough to stay cheap on a render path.
  for (let step = 2; step <= 60; step += 2) {
    const candidate = darken(color, towardsReadable * step)
    if (contrastRatio(candidate, surface) >= target) return candidate
  }
  return darken(color, towardsReadable * 60)
}

/** WCAG contrast ratio between two colours, 1 (identical) → 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const first = wcagLuminance(a)
  const second = wcagLuminance(b)
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

/**
 * WCAG relative luminance, sRGB transfer curve included.
 *
 * Deliberately separate from {@link relativeLuminance} below: that one is the
 * cheap channel average, which is all a light/dark decision needs, and it is
 * load-bearing for `readableInk`. A ratio needs the real curve — the average
 * puts a mid green at 2.2:1 where the true figure is 4.1:1, so a target built
 * on it would pass colours that do not read.
 */
function wcagLuminance(color: string): number {
  const rgb = rgbOf(color)
  if (!rgb) return 0
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return (
    0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
  )
}

/** The channels of a hex or `rgb()` colour, or `null` if it parses as neither. */
function rgbOf(color: string): {r: number; g: number; b: number} | null {
  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i)
  if (hex) {
    const raw =
      hex[1].length === 3
        ? hex[1]
            .split('')
            .map(c => c + c)
            .join('')
        : hex[1].slice(0, 6)
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    }
  }
  const rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  return rgb ? {r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3])} : null
}

/**
 * How much of a category accent shows through a card's border. Low on purpose:
 * the border says *which kind of block this is*, it does not shout.
 */
const CARD_BORDER_TINT = 0.4

/**
 * A block card's border: a category accent at {@link CARD_BORDER_TINT}, laid on
 * the chrome's structural hairline rather than on the page.
 *
 * The card is the same colour as the sheet it sits on, so this hairline is the
 * card's whole *shape* — and a 40% tint painted straight onto the sheet is not
 * enough of one. Measured against the light studio it lands between 1.54:1 and
 * 1.97:1 depending only on the hue, which makes the plain text block (1.61:1),
 * the commonest block in any book, the faintest line in the studio: fainter than
 * the chrome's own recessed hairlines (1.66:1).
 *
 * Painting the same tint onto that hairline instead gives every hue a floor —
 * 2.29:1 in light, 2.42:1 in dim, 2.73:1 in dark — so a block's colour changes
 * how its card reads, never whether it reads at all. The accent is untouched:
 * only what is underneath it changed.
 *
 * Not to be confused with the `accentBorder` *role*, which is a translucent
 * primary for a selected row: this one is a category colour, and it is opaque.
 */
export function cardBorder(accent: string, chrome: StudioChrome): string {
  return tintOn(accent, CARD_BORDER_TINT, chrome.borderStrong)
}

/** `color` at `alpha` painted onto an opaque `base`, flattened to one colour. */
function tintOn(color: string, alpha: number, base: string): string {
  const top = hexToRgb(color)
  const under = hexToRgb(base)
  if (!top || !under) return base
  const mix = (a: number, b: number) => a * alpha + b * (1 - alpha)
  return rgbToHex(mix(top.r, under.r), mix(top.g, under.g), mix(top.b, under.b))
}

/**
 * The ink that reads on an arbitrary background.
 *
 * Some studio furniture does not sit on the studio's own surfaces: the page
 * title is written straight onto the book's *shell* (the colour a FlashBook
 * theme paints behind its paper), and a block preview sits on the theme's page.
 * Those backgrounds belong to the book, not to the app, so their ink cannot come
 * from the chrome — it has to be chosen against the colour actually behind it.
 * Every book theme currently paints a dark shell, but a future light one must
 * not end up with white-on-white.
 */
export function readableInk(background: string): {
  ink: string
  inkMuted: string
  inkFaint: string
} {
  return relativeLuminance(background) < 0.5
    ? {
        ink: '#FFFFFF',
        inkMuted: 'rgba(255,255,255,0.72)',
        inkFaint: 'rgba(255,255,255,0.45)',
      }
    : {
        ink: '#111822',
        inkMuted: 'rgba(17,24,34,0.72)',
        inkFaint: 'rgba(17,24,34,0.45)',
      }
}

/**
 * The cheap luminance of a hex or `rgb()` colour, 0 (black) → 1 (white): the
 * channel average, with no sRGB curve.
 *
 * "Cheap" is the point — this answers the one question `readableInk` asks, which
 * is which side of the middle a background falls on, and it is left alone for
 * that reason. A *ratio* needs {@link contrastRatio} instead.
 */
export function relativeLuminance(color: string): number {
  const rgb = rgbOf(color)
  // Unparseable: assume dark, which is where every book shell lives.
  if (!rgb) return 0
  return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255
}
